import React from 'react';
import Button from '@material-ui/core/Button';

import { createStyles, Theme } from '@material-ui/core';

import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';

//@ts-ignore
import { shorelightColorPalette } from '@shorelight/super-form/utils/ThemeUtils/defaultColorPalette';

import MarkdownRenderer from '@shorelight/ui-common-components/cjs/components/other/MarkdownRenderer';

const styles = (theme: Theme) =>
  createStyles({
    mainContainer: {
      fontWeight: 300,
      display: 'flex',
      flexDirection: 'column' as 'column',
      alignItems: 'center',
      textAlign: 'center' as 'center',
      paddingTop: theme.spacing(7),
      paddingBottom: theme.spacing(7),
      color: shorelightColorPalette.white,
      backgroundColor: shorelightColorPalette.blueExtraDark,
      [theme.breakpoints.down('xs')]: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2)
      }
    },
    title: {
      maxWidth: 700,
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      fontSize: 42,
      [theme.breakpoints.down('xs')]: {
        fontSize: 20
      }
    },
    welcomeContent: {
      width: '70%',
      maxWidth: 680,
      margin: '70px auto',
      [theme.breakpoints.down('xs')]: {
        width: '90%',
        margin: 20
      },
      '& h2': {
        fontSize: 24,
        fontWeight: '400'
      },
      '& p, li': {
        fontWeight: 'lighter',
        lineHeight: '24px'
      }
    },
    roundedButton: {
      padding: '0 16px',
      borderRadius: 40 / 2,
      minHeight: 40,
      textTransform: 'capitalize'
    },
    containedButton: {
      color: shorelightColorPalette.white,
      backgroundColor: shorelightColorPalette.blueExtraDark,
      [theme.breakpoints.down('xs')]: {
        marginBottom: 20
      },
      '&:hover': {
        backgroundColor: shorelightColorPalette.white,
        color: shorelightColorPalette.blueExtraDark,
        border: `1px solid ${shorelightColorPalette.blueExtraDark}`
      }
    },
    buttonBar: {
      marginTop: 72,
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-evenly',
      [theme.breakpoints.down('xs')]: {
        marginTop: 20
      }
    },
    buttonContainer: {
      flexBasis: '100%',
      [theme.breakpoints.up('sm')]: {
        flexBasis: '45%',
        paddingLeft: theme.spacing()
      },
      [theme.breakpoints.up('lg')]: {
        flexBasis: '30%'
      }
    }
  });

interface WelcomepageProps extends WithStyles<typeof styles> {
  onContinue: () => void;
}

const fields = [
  {
    markdown:
      'Welcome to Shorelight!  Shorelight is managing the payment process for your U.S. university program.  Please read below for instructions on making your payment.'
  },
  {
    markdown:
      '## How To Submit Your Payment\n The fastest, easiest, most secure way to make payments is via Flywire: https://www.flywire.com/pay/shorelight/. Make sure to select the program that appears on the student’s program offer letter when initiating a payment.'
  },
  {
    markdown:
      '## The Benefits of Flywire\n - Flywire guarantees the best exchange rates and offers 24/7 multilingual customer service.\n - Flywire provides you with a tracking number so you can track your payment on its website from initiation to receipt.\n - Any payment from outside the United States is accepted via Flywire with unique payment options specialized for your location.'
  },
  {
    markdown:
      '## Prefer to Make a Bank Wire Transfer?\n We also accept bank wire transfers from approved payers in certain countries. **Please note:** To become an approved payer and receive bank wire instructions, you must go through our mandatory screening process. The screening process may take up to 5 business days.'
  },
  {
    markdown:
      '## Payer Screening\n We are required to screen each individual payer before we can provide our bank wire details to accept payment.  A payer must go through the screening process each time the payer makes a direct wire transfer payment.  Please read the instructions below carefully and enter the required information so that we may determine whether you are eligible to pay by direct bank wire.'
  },
  {
    markdown:
      '## How It Works\n - STEP 1: Submit student name and payment ID # (located on the student’s program offer letter) \n\n * STEP 2: Submit the payer name, birth date, contact information of payer.  Please note: a payer is the person who is making payment and whose name is on the account from which the wire is initiated.\n\n * STEP 3: Upload a copy of the payer’s passport/government ID.\n\n * STEP 4: If you are eligible to pay by direct bank wire, you will receive an email with wire payment instructions (this may take up to 5 business days). '
  }
];

const WelcomePage: React.FC<WelcomepageProps> = ({ classes, onContinue }) => {
  return (
    <div data-testid={'welcome-page-container'}>
      <div className={classes.mainContainer}>
        <p className={classes.title}>Mandatory Payer Screening</p>
      </div>
      <div className={classes.welcomeContent}>
        {fields.map(({ markdown }, index) => {
          return <MarkdownRenderer key={index} rawMarkdown={markdown} />;
        })}
        <div className={classes.buttonBar}>
          <div className={classes.buttonContainer}>
            <Button
              size="large"
              color="secondary"
              fullWidth
              className={`${classes.containedButton} ${classes.roundedButton}`}
              href="https://www.flywire.com/pay/shorelight/"
            >
              Pay With Flywire
            </Button>
          </div>
          <div className={classes.buttonContainer}>
            <Button
              size="large"
              color="secondary"
              className={classes.roundedButton}
              variant="outlined"
              fullWidth
              onClick={onContinue}
            >
              Start Payer Screening
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withStyles(styles)(WelcomePage);
